import React, { memo, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import TimeRangeToggle from './TimeRangeToggle';
import { useEngagementInsights } from '../../hooks/useEngagementInsights';
import type { EngagementTimeRange } from '../../utils/engagementAnalytics';

const getInitials = (email: string) => email.charAt(0).toUpperCase();

const MostEngagedRecipients: React.FC = () => {
  const [timeRange, setTimeRange] = useState<EngagementTimeRange>('30d');
  const { data, isLoading, error, refetch, isFetching } = useEngagementInsights(timeRange);

  const recipients = useMemo(() => data?.recipients ?? [], [data]);

  const formattedRecipients = useMemo(
    () =>
      recipients.map((recipient) => ({
        id: recipient.recipient,
        label: recipient.recipient || 'Unknown recipient',
        lastInteractionLabel: recipient.lastInteraction
          ? `Last interaction ${new Date(recipient.lastInteraction).toLocaleDateString()}`
          : 'No timestamp available',
        opens: recipient.opens,
        clicks: recipient.clicks,
      })),
    [recipients]
  );

  return (
    <Card elevation={0} sx={{ height: '100%' }}>
      <CardHeader
        title={<Typography variant="h6">Most Engaged Recipients</Typography>}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <TimeRangeToggle value={timeRange} onChange={setTimeRange} disabled={isLoading} />
            <Tooltip title="Refresh recipients">
              <span>
                <IconButton
                  onClick={() => refetch()}
                  disabled={isFetching}
                  aria-label="Refresh top recipient insights"
                >
                  {isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        }
      />
      <CardContent>
        {error ? (
          <Alert severity="error">Unable to load recipient insights.</Alert>
        ) : isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List aria-label="Most engaged recipients" dense>
            {formattedRecipients.map((recipient) => (
              <ListItem key={recipient.id} sx={{ px: 0 }}>
                <ListItemAvatar>
                  <Avatar>
                    {recipient.label ? getInitials(recipient.label) : <MailOutlineIcon fontSize="small" />}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={recipient.label}
                  secondary={recipient.lastInteractionLabel}
                />
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={`Opens ${recipient.opens}`}
                    size="small"
                    aria-label={`Recipient opens ${recipient.opens}`}
                  />
                  <Chip
                    label={`Clicks ${recipient.clicks}`}
                    size="small"
                    color="success"
                    aria-label={`Recipient clicks ${recipient.clicks}`}
                  />
                </Stack>
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default memo(MostEngagedRecipients);
